import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Circle,
    LayoutGrid as Lists,
  Palette,
  StickyNote,
} from "lucide-react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import TaskNoteEditor from "@/components/tasks/details/TaskNoteEditor/TaskNoteEditor";
import TaskStatusForm from "@/components/tasks/status/TaskStatusForm/TaskStatusForm";
import { TaskDisplayControls, type TaskColorDraft } from "@/components/tasks/options/TaskColorEditor/TaskColorEditor";
import { TaskDescriptionEditor } from "@/components/tasks/details/TaskDescriptionEditor/TaskDescriptionEditor";
import TaskScheduleForm, {
  parseScheduleDuration,
} from "@/components/tasks/schedule/TaskScheduleForm/TaskScheduleForm";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { trpc } from "@/lib/trpc";
import { createTaskScheduleValue } from "@/lib/taskTiming";
import {
  DEFAULT_AREA_COLOR,
  AREA_COLORS,
  resolveTaskColors,
  type ListColorKey,
} from "@/lib/listColors";
import {
  timeStringToMinutes,
  todayKey,
} from "@/lib/time";

type View = "main" | "list" | "notes" | "display";
type Status = "pending" | "done" | "skipped";

const CREATE_SCHEDULE_OPEN_KEY = "create-task-schedule-open";

function readCreateScheduleOpen() {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(CREATE_SCHEDULE_OPEN_KEY) === "true";
}

export default function CreateTaskSheet({
  open,
  onOpenChange,
  date,
  listId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  date?: string | null;
  listId?: number | null;
}) {
  const [, setLocation] = useLocation();
  const utils = trpc.useUtils();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const listsQuery = trpc.lists.list.useQuery();

  const inboxArea = (listsQuery.data ?? []).find(l => l.kind === "inbox");
  const [view, setView] = useState<View>("main");
  const [selectedListId, setSelectedListId] = useState<number | null>(() =>
    listId ?? getLastUsedListId(),
  );
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [editDescription, setEditDescription] = useState(false);
  const [notes, setNotes] = useState("");
  const [scheduleOpen, setScheduleOpen] = useState(readCreateScheduleOpen);
  const [dateDraft, setDateDraft] = useState("");
  const [startTime, setStartTime] = useState("");
  const [durationMin, setDurationMin] = useState("");
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [status, setStatus] = useState<Status>("pending");
  const [colorDraft, setColorDraft] = useState<TaskColorDraft>({
    inheritView: true,
    barColor: null,
    bgColor: null,
  });

  const currentArea =
    (listsQuery.data ?? []).find(l => l.id === selectedListId) ??
    inboxArea ??
    null;
  const listColor = (currentArea?.color as ListColorKey | null) ?? DEFAULT_AREA_COLOR;
  const listBgColor = (currentArea?.bgColor as ListColorKey | null) ?? null;
  const colors = resolveTaskColors({
    barColor: colorDraft.inheritView ? null : colorDraft.barColor,
    bgColor: colorDraft.inheritView ? null : colorDraft.bgColor,
    listColor,
    listBgColor,
  });
  const listColors = resolveTaskColors({
    barColor: null,
    bgColor: null,
    listColor,
    listBgColor,
  });
  const startMinutes = startTime ? timeStringToMinutes(startTime) : null;
  const durationValue = parseScheduleDuration(durationMin);
  const hasScheduleDraft = Boolean(dateDraft || startTime || durationValue || (scheduleOpen && alertEnabled));
  const hasCustomColors = !colorDraft.inheritView;
  const titleEmpty = title.trim() === "";
  const targetDate = dateDraft || null;

  useEffect(() => {
    if (!open) return;
    const savedListId = listId ?? getLastUsedListId();
    setView("main");
    setSelectedListId(savedListId);
    setTitle("");
    setDetails("");
    setEditDescription(false);
    setNotes("");
    setScheduleOpen(readCreateScheduleOpen());
    setDateDraft(date ?? "");
    setStartTime("");
    setDurationMin("");
    setAlertEnabled(false);
    setStatus("pending");
    setColorDraft({ inheritView: true, barColor: null, bgColor: null });
    const timer = window.setTimeout(() => inputRef.current?.focus(), 180);
    return () => window.clearTimeout(timer);
  }, [open, date, listId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(CREATE_SCHEDULE_OPEN_KEY, String(scheduleOpen));
  }, [scheduleOpen]);

  const createTask = trpc.tasks.create.useMutation({
    onSuccess: () => {
      if (targetDate) utils.day.list.invalidate({ date: targetDate });
      utils.tasks.listInbox.invalidate();
      utils.tasks.listByList.invalidate();
      utils.lists.overview.invalidate();
      utils.day.monthSummary.invalidate();
      saveLastUsedArea(currentArea?.id ?? null);
      onOpenChange(false);
    },
    onError: e => toast.error(e.message),
  });

  function addTemplate() {
    const areaSegment = currentArea ? `/${currentArea.id}` : "";
    onOpenChange(false);
    setLocation(`/day/${dateDraft || date || todayKey()}/add${areaSegment}`);
  }

  function create() {
    const nextTitle = title.trim();
    if (!nextTitle || createTask.isPending) return;
    createTask.mutate({
      title: nextTitle,
      date: targetDate,
      listId: currentArea?.id ?? selectedListId ?? undefined,
      startTime: hasScheduleDraft ? startTime || null : null,
      details: details.trim() || null,
      notes: notes.trim() || null,
      durationMin: hasScheduleDraft ? durationValue : null,
      status,
      alertEnabled: hasScheduleDraft ? alertEnabled : false,
      barColor: colorDraft.inheritView ? null : colorDraft.barColor,
      bgColor: colorDraft.inheritView ? null : colorDraft.bgColor,
    });
  }

  const isSubView = view !== "main";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl flex max-h-[92dvh] flex-col overflow-hidden"
        showClose={false}
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
          <div
            className="flex w-full transition-transform duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]"
            style={{ transform: isSubView ? "translateX(-100%)" : "translateX(0%)" }}
          >
            <div className="w-full shrink-0">
              <div className="mx-auto w-full max-w-2xl">
                <SheetHeader className="text-left">
                  <div className="flex w-full items-center justify-between gap-3 px-1 pb-2">
                    <button
                      type="button"
                      onClick={() => setView("list")}
                      className="inline-flex max-w-48 shrink-0 items-center gap-2 rounded-full px-3 py-1 text-xs uppercase tracking-[0.14em] truncate hover:opacity-80 transition-opacity"
                      style={{
                        background: `color-mix(in oklch, ${listColors.bar} 18%, transparent)`,
                        color: listColors.ink,
                      }}
                    >
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ background: listColors.bar }}
                        aria-hidden
                      />
                      {currentArea?.name ?? "Inbox"}
                    </button>
                    {hasCustomColors && (
                      <button
                        type="button"
                        onClick={() => setView("display")}
                        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-opacity hover:opacity-80"
                        style={{ background: colors.soft, color: colors.ink }}
                        aria-label="Edit custom display colors"
                      >
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ background: colors.bar }}
                          aria-hidden
                        />
                      </button>
                    )}
                  </div>

                  <div className="px-1 pb-3 space-y-1">
                    <SheetTitle asChild>
                      <textarea
                        ref={inputRef}
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        rows={1}
                        className="w-full resize-none bg-transparent font-display text-2xl leading-tight outline-none focus:outline-none placeholder:text-muted-foreground/40 overflow-hidden"
                        style={{ color: colors.ink }}
                        placeholder="Task title"
                        onInput={e => {
                          const el = e.currentTarget;
                          el.style.height = "auto";
                          el.style.height = `${el.scrollHeight}px`;
                        }}
                      />
                    </SheetTitle>
                    <TaskDescriptionEditor
                      value={details}
                      savedValue=""
                      onChange={setDetails}
                      onCommit={value => setDetails(value ?? "")}
                      editDescription={editDescription}
                      onEditDescriptionChange={setEditDescription}
                      alwaysVisible
                    />
                  </div>
                </SheetHeader>

                <div className="space-y-3 px-1 pb-6">
                  <TaskScheduleForm
                    open={scheduleOpen}
                    onOpenChange={setScheduleOpen}
                    value={createTaskScheduleValue({ date: dateDraft, startTime, durationMinutes: durationMin, alertEnabled })}
                    onChange={next => {
                      setDateDraft(next.timing.startDate);
                      setStartTime(next.timing.startTime);
                      setDurationMin(next.timing.durationMinutes);
                      setAlertEnabled(next.alertEnabled);
                    }}
                    colors={colors}
                  />

                  <button
                    type="button"
                    onClick={() => setView("notes")}
                    className="flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-sm transition-colors hover:bg-accent/20"
                    style={{
                      color: colors.ink,
                      borderColor: `color-mix(in oklch, ${colors.bar} 22%, transparent)`,
                      background: `color-mix(in oklch, ${colors.bar} 6%, transparent)`,
                    }}
                  >
                    <StickyNote className="h-4 w-4 shrink-0" style={{ color: colors.ink, opacity: 0.6 }} />
                    <span className="flex-1 text-left truncate">
                      {notes.trim() ? notes.trim() : "Add a note"}
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0" style={{ opacity: 0.5 }} />
                  </button>

                  <button
                    type="button"
                    onClick={() => setView("display")}
                    className="flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-sm transition-colors hover:bg-accent/20"
                    style={{
                      color: colors.ink,
                      borderColor: `color-mix(in oklch, ${colors.bar} 22%, transparent)`,
                      background: `color-mix(in oklch, ${colors.bar} 6%, transparent)`,
                    }}
                  >
                    <Palette className="h-4 w-4 shrink-0" style={{ color: colors.ink, opacity: 0.6 }} />
                    <span className="flex-1 text-left">
                      {hasCustomColors ? "Custom display" : "Custom display"}
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0" style={{ opacity: 0.5 }} />
                  </button>

                  <button
                    type="button"
                    onClick={addTemplate}
                    disabled={createTask.isPending}
                    className="flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-sm transition-colors hover:bg-accent/20 disabled:opacity-50"
                    style={{
                      color: colors.ink,
                      borderColor: `color-mix(in oklch, ${colors.bar} 22%, transparent)`,
                    }}
                  >
                    <Lists className="h-4 w-4 shrink-0" style={{ color: colors.ink, opacity: 0.6 }} />
                    <span className="flex-1 text-left">Add from template</span>
                    <ChevronRight className="h-4 w-4 shrink-0" style={{ opacity: 0.5 }} />
                  </button>


                  {/* <TaskStatusForm
                    status={status}
                    startedAt={null}
                    completedAt={null}
                    notes={null}
                    log={null}
                    startMinutes={startMinutes}
                    durationMin={durationValue}
                    colors={colors}
                    onStatusChange={setStatus}
                  /> */}

                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-12 bg-muted/60 hover:bg-muted"
                      onClick={() => onOpenChange(false)}
                      disabled={createTask.isPending}
                      style={{ color: colors.ink }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={create}
                      disabled={titleEmpty || createTask.isPending}
                      className="h-12"
                      style={{ background: colors.bar, color: colors.soft }}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full shrink-0">
              {view === "list" && (
                <AreaView
                  currentListId={currentArea?.id ?? selectedListId}
                  onPick={nextListId => {
                    setSelectedListId(nextListId);
                    saveLastUsedArea(nextListId);
                    setView("main");
                  }}
                  onBack={() => setView("main")}
                />
              )}
              {view === "notes" && (
                <NoteView
                  colors={colors}
                  savedValue={notes}
                  onSave={value => {
                    setNotes(value.trim());
                    setView("main");
                  }}
                  onBack={() => setView("main")}
                />
              )}
              {view === "display" && (
                <DisplayView
                  colors={colors}
                  colorDraft={colorDraft}
                  setColorDraft={setColorDraft}
                  title={title}
                  listName={currentArea?.name ?? "Inbox"}
                  fallbackBar={listColor}
                  fallbackBg={listBgColor}
                  onBack={() => setView("main")}
                />
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function AreaView({
  currentListId,
  onPick,
  onBack,
}: {
  currentListId: number | null;
  onPick: (listId: number | null) => void;
  onBack: () => void;
}) {
  const listsQuery = trpc.lists.list.useQuery();

  return (
    <div className="mx-auto w-full max-w-2xl pb-6">
      <SubViewHeader icon={<Circle className="h-5 w-5" />} title="List" onBack={onBack} />
      <div className="max-h-96 overflow-y-auto space-y-1 px-1">
        {(listsQuery.data ?? []).map(list => {
          const color = AREA_COLORS[list.color as ListColorKey] ?? AREA_COLORS[DEFAULT_AREA_COLOR];
          const active = list.id === currentListId;
          return (
            <button
              key={list.id}
              type="button"
              onClick={() => onPick(list.id)}
              className={`w-full inline-flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left text-sm transition-colors ${active ? "font-semibold bg-accent/50" : "hover:bg-accent/30"}`}
            >
              <span
                className="h-2.5 w-2.5 rounded-full shrink-0"
                style={{ background: color.base }}
                aria-hidden
              />
              <span className="flex-1 truncate">{list.name}</span>
              {active && <Check className="h-3.5 w-3.5 text-foreground" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NoteView({
  colors,
  savedValue,
  onSave,
  onBack,
}: {
  colors: { bar: string; soft: string; ink: string };
  savedValue: string;
  onSave: (value: string) => void;
  onBack: () => void;
}) {
  const [draft, setDraft] = useState(savedValue);

  useEffect(() => {
    setDraft(savedValue);
  }, [savedValue]);

  const handleBack = () => {
    if (draft !== savedValue) onSave(draft);
    onBack();
  };

  return (
    <div className="mx-auto w-full max-w-2xl pb-6">
      <SubViewHeader
        icon={<StickyNote className="h-5 w-5" style={{ color: colors.ink, opacity: 0.7 }} />}
        title={savedValue.trim() ? "Edit note" : "Add note"}
        onBack={handleBack}
      />
      <div className="px-1">
        <TaskNoteEditor
          savedValue={savedValue}
          onDraftChange={setDraft}
          onSave={onSave}
          colors={colors}
        />
      </div>
    </div>
  );
}

function DisplayView({
  colors,
  colorDraft,
  setColorDraft,
  title,
  listName,
  fallbackBar,
  fallbackBg,
  onBack,
}: {
  colors: { bar: string; soft: string; ink: string };
  colorDraft: TaskColorDraft;
  setColorDraft: (value: TaskColorDraft) => void;
  title: string;
  listName: string;
  fallbackBar: ListColorKey;
  fallbackBg: ListColorKey | null;
  onBack: () => void;
}) {
  return (
    <div className="mx-auto w-full max-w-2xl pb-6">
      <SubViewHeader
        icon={<Palette className="h-5 w-5" style={{ color: colors.ink, opacity: 0.7 }} />}
        title="Display"
        onBack={onBack}
      />
      <div className="px-1">
        <TaskDisplayControls
          value={colorDraft}
          onChange={setColorDraft}
          previewTitle={title}
          previewLabel={listName}
          fallbackBar={fallbackBar}
          fallbackBg={fallbackBg}
          accent={colors}
        />
      </div>
    </div>
  );
}

function SubViewHeader({
  icon,
  title,
  onBack,
}: {
  icon: React.ReactNode;
  title: string;
  onBack: () => void;
}) {
  return (
    <div className="flex items-center gap-3 px-1 pb-4 pt-4">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-accent/40 transition-colors"
        aria-label="Back"
      >
        <ArrowLeft className="h-4 w-4" />
      </button>
      {icon}
      <span className="font-display text-2xl tracking-tight text-foreground">{title}</span>
    </div>
  );
}

function getLastUsedListId(): number | null {
  if (typeof localStorage === "undefined") return null;
  const saved = localStorage.getItem("lastUsedListId");
  if (!saved) return null;
  const parsed = parseInt(saved, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function saveLastUsedArea(id: number | null | undefined) {
  if (!id) return;
  localStorage.setItem("lastUsedListId", String(id));
}
